import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { X, Instagram, Youtube, MessageCircle } from 'lucide-react';

export default function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  return (
    <>
      <footer className="border-t border-slate-200 mt-16 bg-white">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 py-8">
          {/* Social Media Links */}
          <div className="flex justify-center gap-6 mb-6">
            <a 
              href="https://www.instagram.com/myeai_global/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#337AF7]/10 flex items-center justify-center text-[#162959]/60 hover:text-[#337AF7] transition-all duration-200"
              data-testid="footer-instagram-link"
              aria-label="Follow us on Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a 
              href="https://www.youtube.com/@MYeAI_Global" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#337AF7]/10 flex items-center justify-center text-[#162959]/60 hover:text-[#337AF7] transition-all duration-200"
              data-testid="footer-youtube-link"
              aria-label="Subscribe to our YouTube channel"
            >
              <Youtube className="h-5 w-5" />
            </a>
            <a 
              href="https://chat.whatsapp.com/Dp0q9F6Z3TZJLOAVsXugNE" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#337AF7]/10 flex items-center justify-center text-[#162959]/60 hover:text-[#337AF7] transition-all duration-200"
              data-testid="footer-whatsapp-link"
              aria-label="Join our WhatsApp Community"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
          </div>

          {/* Policy Links */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <button 
                onClick={() => setPrivacyOpen(true)}
                className="text-sm text-[#162959]/60 hover:text-[#337AF7] transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setTermsOpen(true)}
                className="text-sm text-[#162959]/60 hover:text-[#337AF7] transition-colors"
                data-testid="footer-terms-link"
              >
                Terms & Conditions
              </button>
              <button 
                onClick={() => setRefundOpen(true)}
                className="text-sm text-[#162959]/60 hover:text-[#337AF7] transition-colors"
                data-testid="footer-refund-link"
              >
                Purchase & Refund Policy
              </button>
              <a href="mailto:legal@myeaitradingcompass.com" className="text-sm text-[#162959]/60 hover:text-[#337AF7] transition-colors">
                Contact
              </a>
            </div>
            <div className="text-sm text-[#162959]/60">
              © 2025 MYEAI LTD. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent 
          className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white"
          data-testid="privacy-policy-modal"
        >
          <DialogHeader className="sticky top-0 bg-white pb-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold text-[#162959]">
                Privacy Policy
              </DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="py-6 space-y-6 text-[#162959]">
            {/* Company Info */}
            <div className="space-y-1">
              <p><span className="font-semibold">Company:</span> MYEAI LTD</p>
              <p><span className="font-semibold">Data Controller:</span> MYEAI LTD</p>
              <p><span className="font-semibold">Contact:</span> <a href="mailto:privacy@myeaitradingcompass.com" className="text-[#337AF7] hover:underline">privacy@myeaitradingcompass.com</a></p>
            </div>

            {/* Section 1 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">1. Data We Collect</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80">
                <li>Identity & contact data</li>
                <li>Account & subscription data</li>
                <li>Usage analytics</li>
                <li>Affiliate tracking data</li>
              </ul>
              <p className="mt-2 text-sm text-[#162959]/70 italic">We do not process special category personal data.</p>
            </div>

            {/* Section 2 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">2. Lawful Bases for Processing</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2 border-b border-slate-200 font-semibold">Data Type</th>
                      <th className="text-left px-4 py-2 border-b border-slate-200 font-semibold">Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100">Account data</td>
                      <td className="px-4 py-2 border-b border-slate-100">Contract</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100">Payments</td>
                      <td className="px-4 py-2 border-b border-slate-100">Contract & legal obligation</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100">Analytics</td>
                      <td className="px-4 py-2 border-b border-slate-100">Legitimate interest</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100">Marketing</td>
                      <td className="px-4 py-2 border-b border-slate-100">Consent</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">Affiliate tracking</td>
                      <td className="px-4 py-2">Legitimate interest</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">3. Automated Processing</h3>
              <p className="text-[#162959]/80">We use automated systems for analytics and platform optimisation.</p>
              <p className="text-[#162959]/80 mt-1">These processes do not produce legal or similarly significant effects.</p>
            </div>

            {/* Section 4 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">4. Data Retention</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80">
                <li><span className="font-medium">Account data:</span> duration of account</li>
                <li><span className="font-medium">Analytics:</span> up to 24 months</li>
                <li><span className="font-medium">Financial records:</span> 7 years (UK law)</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">5. International Transfers</h3>
              <p className="text-[#162959]/80">Some service providers process data outside the UK/EEA.</p>
              <p className="text-[#162959]/80 mt-1">All transfers are protected using UK GDPR Standard Contractual Clauses.</p>
            </div>

            {/* Section 6 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">6. Your Rights</h3>
              <p className="text-[#162959]/80 mb-2">You may:</p>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80">
                <li>Access, correct, or delete your data</li>
                <li>Object to processing</li>
                <li>Withdraw consent</li>
                <li>Lodge a complaint with the ICO (<a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#337AF7] hover:underline">www.ico.org.uk</a>)</li>
              </ul>
            </div>

            {/* Section 7 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">7. Contact</h3>
              <p className="text-[#162959]/80"><span className="font-medium">Data Protection Officer</span></p>
              <p className="text-[#162959]/80">Email: <a href="mailto:privacy@myeaitradingcompass.com" className="text-[#337AF7] hover:underline">privacy@myeaitradingcompass.com</a></p>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-slate-200 text-center">
              <p className="text-sm text-[#162959]/60">© 2025 MYEAI LTD. All Rights Reserved.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms and Conditions Modal */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent 
          className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white"
          data-testid="terms-conditions-modal"
        >
          <DialogHeader className="sticky top-0 bg-white pb-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold text-[#162959]">
                Terms and Conditions
              </DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="py-6 space-y-6 text-[#162959]">
            {/* Header Info */}
            <div className="space-y-1 text-sm text-[#162959]/80">
              <p><span className="font-semibold text-[#162959]">Effective Date:</span> October 6, 2025</p>
              <p><span className="font-semibold text-[#162959]">Company:</span> MYEAI LTD (Company Number 16297507)</p>
              <p><span className="font-semibold text-[#162959]">Platform:</span> MYEAI Trading Compass</p>
              <p><span className="font-semibold text-[#162959]">Website:</span> <a href="https://myeaitradingcompass.com" target="_blank" rel="noopener noreferrer" className="text-[#337AF7] hover:underline">https://myeaitradingcompass.com</a></p>
            </div>

            {/* Section 1 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">1. Introduction & Regulatory Status</h3>
              <p className="text-[#162959]/80 mb-3">MYEAI Trading Compass is owned and operated by MYEAI LTD, a company registered in England and Wales.</p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3">
                <p className="font-semibold text-amber-800 mb-2">IMPORTANT REGULATORY NOTICE:</p>
                <ul className="list-disc list-inside space-y-1 text-amber-800 text-sm">
                  <li>MYEAI LTD is not authorised or regulated by the Financial Conduct Authority (FCA).</li>
                  <li>The platform does not provide financial advice, investment advice, portfolio management, brokerage services, or any regulated financial activity.</li>
                  <li>All content, analytics, tools, and insights provided through the platform are educational and informational only.</li>
                </ul>
              </div>
              
              <p className="text-[#162959]/80">By accessing or using the platform, you agree to these Terms and Conditions.</p>
              
              <div className="mt-3">
                <p className="font-medium text-[#162959] mb-1">Related Policies</p>
                <p className="text-[#162959]/80 text-sm">These Terms incorporate by reference the MYEAI Privacy Policy and the Purchase & Refund Policy.</p>
                <p className="text-[#162959]/80 text-sm">By using the Platform, you agree to comply with all applicable policies.</p>
              </div>
            </div>

            {/* Section 2 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">2. Definitions</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li><span className="font-medium">"Company"</span> means MYEAI LTD</li>
                <li><span className="font-medium">"Platform"</span> means the MYEAI Trading Compass software and services</li>
                <li><span className="font-medium">"User"</span> means any person accessing the platform</li>
                <li><span className="font-medium">"Subscription"</span> means a paid access plan</li>
                <li><span className="font-medium">"Content"</span> means educational materials, analytics, tools, and insights</li>
              </ul>
              <p className="text-[#162959]/70 text-sm mt-2 italic">The platform does not provide personalised recommendations or executable trading instructions.</p>
            </div>

            {/* Section 3 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">3. Eligibility</h3>
              <p className="text-[#162959]/80">You must be 18 years or older to use the platform.</p>
              <p className="text-[#162959]/80 mt-1">By registering, you confirm all information provided is accurate and lawful.</p>
            </div>

            {/* Section 4 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">4. Account Security</h3>
              <p className="text-[#162959]/80">You are responsible for safeguarding your login credentials.</p>
              <p className="text-[#162959]/80 mt-1">MYEAI LTD is not responsible for losses caused by unauthorised access due to your failure to secure your account.</p>
            </div>

            {/* Section 5 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">5. Subscriptions & Payments</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>Subscriptions are billed on a recurring basis unless cancelled.</li>
                <li>Prices are displayed in USD or GBP and may include applicable taxes.</li>
                <li>You may cancel your subscription at any time via your account dashboard or by contacting support.</li>
              </ul>
              
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="font-medium text-[#162959] mb-1">Cooling-Off Rights</p>
                <p className="text-[#162959]/80 text-sm">By purchasing digital content and requesting immediate access, you expressly consent to immediate performance and acknowledge that you lose your 14-day statutory right to cancel under the Consumer Contracts Regulations 2013.</p>
              </div>
            </div>

            {/* Section 6 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">6. Acceptable Use</h3>
              <p className="text-[#162959]/80 mb-2">Users must not:</p>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>Misuse or attempt to disrupt the platform</li>
                <li>Copy, scrape, or resell content</li>
                <li>Misrepresent the platform or its services</li>
                <li>Engage in unlawful or misleading conduct</li>
              </ul>
            </div>

            {/* Section 6A */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">6A. Affiliate Conduct Requirements</h3>
              <p className="text-[#162959]/80 mb-2">Affiliates promoting the Platform must comply with all applicable laws, including UK financial promotion rules under the Financial Services and Markets Act 2000.</p>
              <p className="text-[#162959]/80 mb-2">Affiliates must not:</p>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>Imply that MYEAI LTD is FCA-authorised</li>
                <li>Make statements guaranteeing profits or future performance</li>
                <li>Target vulnerable individuals</li>
                <li>Make misleading or exaggerated claims about trading outcomes</li>
              </ul>
              <p className="text-[#162959]/80 text-sm mt-2">MYEAI LTD may terminate any affiliate arrangement that breaches these requirements.</p>
            </div>

            {/* Section 7 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">7. Intellectual Property</h3>
              <p className="text-[#162959]/80">All platform content, systems, and branding are owned by MYEAI LTD.</p>
              <p className="text-[#162959]/80 mt-1">No licence is granted except for personal, non-commercial use.</p>
            </div>

            {/* Section 8 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">8. Financial & Trading Disclaimer</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>MYEAI LTD does not provide investment advice.</li>
                <li>Trading and financial markets involve risk.</li>
                <li>You are solely responsible for your decisions and outcomes.</li>
              </ul>
            </div>

            {/* Section 8A */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">8A. AI and Automated Output Disclaimer</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>The Platform may generate analytical outputs using automated systems.</li>
                <li>These outputs may be incomplete, outdated, or inaccurate.</li>
                <li>They must not be relied upon as a basis for investment decisions.</li>
                <li>Users should independently verify all information and seek professional advice where necessary.</li>
              </ul>
            </div>

            {/* Section 8B */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">8B. Risk Disclosure</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm font-medium">Trading financial markets involves significant risk, including the potential loss of all capital.</p>
                <p className="text-red-800 text-sm mt-1">A full Risk Disclosure Statement is available on the Platform and forms part of these Terms.</p>
                <p className="text-red-800 text-sm mt-1">By using the Platform, you confirm you have read and understood the Risk Disclosure Statement.</p>
              </div>
            </div>

            {/* Section 9 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">9. Limitation of Liability</h3>
              <p className="text-[#162959]/80 mb-2">Nothing in these Terms limits liability for:</p>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>Death or personal injury caused by negligence</li>
                <li>Fraud or fraudulent misrepresentation</li>
                <li>Any liability that cannot be excluded under UK law</li>
              </ul>
              <p className="text-[#162959]/80 text-sm mt-2">Otherwise, MYEAI LTD's total liability is limited to fees paid in the previous 3 months.</p>
            </div>

            {/* Section 10 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">10. Termination</h3>
              <p className="text-[#162959]/80">We may suspend or terminate accounts for breach of these Terms.</p>
              <p className="text-[#162959]/80 mt-1">You may terminate your account at any time.</p>
            </div>

            {/* Section 11A */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">11A. Changes to These Terms</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>We may update these Terms from time to time.</li>
                <li>Where changes are material, we will provide notice via email or through the Platform.</li>
                <li>Continued use of the Platform after the effective date of updated Terms constitutes acceptance of the revised Terms.</li>
                <li>If you do not agree to the updated Terms, you must discontinue use.</li>
              </ul>
            </div>

            {/* Section 11 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">11. Governing Law</h3>
              <p className="text-[#162959]/80">These Terms are governed by the laws of England and Wales.</p>
              <p className="text-[#162959]/80 mt-1">Users retain the right to bring claims before UK courts.</p>
            </div>

            {/* Section 12 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">12. Contact</h3>
              <div className="text-[#162959]/80 text-sm space-y-1">
                <p className="font-medium text-[#162959]">MYEAI LTD</p>
                <p>24A Church Street, Enfield, England, EN2 6BE</p>
                <p>Email: <a href="mailto:legal@myeaitradingcompass.com" className="text-[#337AF7] hover:underline">legal@myeaitradingcompass.com</a></p>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-slate-200 text-center">
              <p className="text-sm text-[#162959]/60">© 2025 MYEAI LTD. All Rights Reserved.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Purchase & Refund Policy Modal */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent 
          className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white"
          data-testid="refund-policy-modal"
        >
          <DialogHeader className="sticky top-0 bg-white pb-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold text-[#162959]">
                Purchase & Refund Policy
              </DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="py-6 space-y-6 text-[#162959]">
            {/* Section 1 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">1. Digital Services</h3>
              <p className="text-[#162959]/80">MYEAI Trading Compass provides digital subscription access to educational and analytical content.</p>
            </div>

            {/* Section 2 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">2. Cooling-Off Rights</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-[#162959]/80">If you request immediate access to digital content, you consent to immediate performance and acknowledge that you lose your 14-day right to cancel.</p>
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">3. Refunds</h3>
              <p className="text-[#162959]/80 mb-2">Refunds may be issued if:</p>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>The service is materially defective</li>
                <li>Access is not provided as described</li>
                <li>Required under UK consumer law</li>
              </ul>
              <p className="text-[#162959]/70 text-sm mt-3 italic">Refunds are not provided for change of mind after access has begun.</p>
            </div>

            {/* Section 4 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">4. Subscription Cancellations</h3>
              <ul className="list-disc list-inside space-y-1 text-[#162959]/80 text-sm">
                <li>You may cancel anytime before renewal.</li>
                <li>No further charges will apply after cancellation.</li>
                <li>Access continues until the end of the billing period.</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">5. Failed Payments & Chargebacks</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 text-sm">We reserve the right to suspend accounts for failed payments or fraudulent chargebacks.</p>
              </div>
            </div>

            {/* Section 6 */}
            <div>
              <h3 className="text-lg font-semibold text-[#337AF7] mb-2">6. Contact</h3>
              <p className="text-[#162959]/80">Email: <a href="mailto:billing@myeaitradingcompass.com" className="text-[#337AF7] hover:underline">billing@myeaitradingcompass.com</a></p>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-slate-200 text-center">
              <p className="text-sm text-[#162959]/60">© 2025 MYEAI LTD. All Rights Reserved.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}